/**
 * Day planning: suggest → she edits → check.
 *
 * A plan is intent only. It never produces events and never touches history; only scans on
 * the day do. Both the suggestion and her edited version go through the same `checkPlan`,
 * which uses the same verdicts as the scanner.
 *
 * The route is the list of spots she will swap that day, in driving order. A pot collected
 * at an earlier stop rides along in the car and can go into a later spot (a "hand-on"),
 * which means fewer pots to load the night before.
 */
import type { CalendarDate } from "./calendar";
import type { Catalog } from "./catalog";
import type { Assessor, Verdict } from "./assess";
import type { History } from "./history";

export interface Route {
  readonly on: CalendarDate;
  /** Spots to swap, in driving order. */
  readonly slotIds: readonly string[];
}

export interface DayPlan {
  readonly on: CalendarDate;
  /** Same order as the route. `potId: null` means "not chosen yet". */
  readonly swaps: readonly { readonly slotId: string; readonly potId: string | null }[];
  /** Extra stock pots to load in case a client refuses one. */
  readonly spares: readonly string[];
}

export interface PlannerInput {
  readonly catalog: Catalog;
  readonly history: History;
  readonly assessor: Assessor;
}

export type PotSource = "stock" | "collected-earlier" | "not-in-car";

export interface SwapReport {
  readonly slotId: string;
  readonly clientId: string;
  /** The pot standing there now, which she takes away during this swap. */
  readonly collects?: string;
  readonly potId: string | null;
  readonly source?: PotSource;
  /** For a hand-on: the spot it was collected from earlier on the route. */
  readonly collectedFromSlotId?: string;
  readonly verdict?: Verdict;
}

export type PlanProblem =
  | { readonly code: "slot-unfilled"; readonly slotId: string }
  | { readonly code: "slot-visited-twice"; readonly slotId: string }
  | { readonly code: "pot-used-twice"; readonly potId: string; readonly slotIds: readonly string[] }
  | { readonly code: "pot-not-in-car"; readonly potId: string; readonly slotId: string; readonly standsAtSlotId: string }
  | { readonly code: "red-verdict"; readonly potId: string; readonly slotId: string }
  | { readonly code: "spare-not-in-stock"; readonly potId: string };

/** A spot on the route where a spare could go if a client refuses the planned pot. */
export interface SpareFit {
  readonly slotId: string;
  readonly clientId: string;
  /** Green, or amber only because her records don't yet reach back a full window. */
  readonly light: "green" | "amber";
}

export interface SpareReport {
  readonly potId: string;
  /** Route spots (driving order) the spare meets the requirements of and isn't blocked from. */
  readonly fits: readonly SpareFit[];
}

export interface PlanReport {
  readonly swaps: readonly SwapReport[];
  /** Each spare in stock and the route spots it could stand in for. */
  readonly spares: readonly SpareReport[];
  /** Stock pots to load the night before: the plan's pots plus spares, sorted. */
  readonly loadList: readonly string[];
  /** Pots collected on the route that go straight into a later spot. */
  readonly handOns: readonly { readonly potId: string; readonly fromSlotId: string; readonly toSlotId: string }[];
  /** Pots collected today that come home. */
  readonly returnsToStock: readonly string[];
  readonly problems: readonly PlanProblem[];
}

/** Pots she can load: active, not written off, and not standing at a client. */
export function inStock(catalog: Catalog, history: History): string[] {
  return catalog.pots.filter((p) => p.active && !history.writtenOff(p.id) && !history.whereIs(p.id)).map((p) => p.id);
}

const byPotNumber = new Intl.Collator("en", { numeric: true }).compare;

/** Where a pot comes from if it's used at route position `index`. */
function sourceAt(
  potId: string,
  index: number,
  history: History,
  routeIndex: ReadonlyMap<string, number>,
): { source: PotSource; standsAtSlotId?: string } {
  const standing = history.whereIs(potId);
  if (!standing) return { source: "stock" };
  const collectedAt = routeIndex.get(standing.slotId);
  if (collectedAt !== undefined && collectedAt < index) {
    return { source: "collected-earlier", standsAtSlotId: standing.slotId };
  }
  return { source: "not-in-car", standsAtSlotId: standing.slotId };
}

/** A hand-on pot is "recorded elsewhere" only until its earlier collection, so that warning doesn't apply. */
function verdictFor(assessor: Assessor, potId: string, slotId: string, on: CalendarDate, source: PotSource): Verdict {
  const verdict = assessor.assess(potId, slotId, on);
  if (source !== "collected-earlier") return verdict;
  const reasons = verdict.reasons.filter((r) => r.code !== "recorded-elsewhere");
  return { light: reasons[0]?.light ?? "green", reasons };
}

function firstIndexByPosition(slotIds: readonly string[]): Map<string, number> {
  const index = new Map<string, number>();
  slotIds.forEach((slotId, i) => {
    if (!index.has(slotId)) index.set(slotId, i);
  });
  return index;
}

export function checkPlan(plan: DayPlan, { catalog, history, assessor }: PlannerInput): PlanReport {
  const problems: PlanProblem[] = [];
  const routeIndex = firstIndexByPosition(plan.swaps.map((s) => s.slotId));
  const seenSlots = new Set<string>();
  const usedAt = new Map<string, string[]>();

  const swaps: SwapReport[] = plan.swaps.map(({ slotId, potId }, i) => {
    const slot = catalog.slot(slotId);
    if (seenSlots.has(slotId)) problems.push({ code: "slot-visited-twice", slotId });
    seenSlots.add(slotId);

    const collects = history.occupantOf(slotId)?.potId;
    const base = { slotId, clientId: slot.clientId, potId, ...(collects ? { collects } : {}) };
    if (potId === null) {
      problems.push({ code: "slot-unfilled", slotId });
      return base;
    }

    usedAt.set(potId, [...(usedAt.get(potId) ?? []), slotId]);
    const { source, standsAtSlotId } = sourceAt(potId, i, history, routeIndex);
    if (source === "not-in-car" && standsAtSlotId) {
      problems.push({ code: "pot-not-in-car", potId, slotId, standsAtSlotId });
    }
    const verdict = verdictFor(assessor, potId, slotId, plan.on, source);
    if (verdict.light === "red") problems.push({ code: "red-verdict", potId, slotId });

    return {
      ...base,
      source,
      ...(source === "collected-earlier" && standsAtSlotId ? { collectedFromSlotId: standsAtSlotId } : {}),
      verdict,
    };
  });

  for (const [potId, slotIds] of usedAt) {
    if (slotIds.length > 1) problems.push({ code: "pot-used-twice", potId, slotIds });
  }

  const fromStock = new Set(swaps.filter((s) => s.source === "stock" && s.potId).map((s) => s.potId as string));
  for (const spare of plan.spares) {
    if (!inStock(catalog, history).includes(spare)) problems.push({ code: "spare-not-in-stock", potId: spare });
    else fromStock.add(spare);
  }

  const handOns = swaps
    .filter((s) => s.source === "collected-earlier" && s.potId && s.collectedFromSlotId)
    .map((s) => ({ potId: s.potId as string, fromSlotId: s.collectedFromSlotId as string, toSlotId: s.slotId }));
  const handedOn = new Set(handOns.map((h) => h.potId));
  const returnsToStock = swaps
    .flatMap((s) => (s.collects && !handedOn.has(s.collects) ? [s.collects] : []))
    .sort(byPotNumber);

  const routeSlots = [...routeIndex.keys()];
  const spares: SpareReport[] = plan.spares
    .filter((potId) => fromStock.has(potId))
    .map((potId) => ({
      potId,
      fits: routeSlots.flatMap((slotId): SpareFit[] => {
        const verdict = assessor.assess(potId, slotId, plan.on);
        // Fits = not blocked and meets her client's requirements; unconfirmed history alone doesn't rule it out.
        if (verdict.reasons.some((r) => r.code !== "history-unverified")) return [];
        return [{ slotId, clientId: catalog.slot(slotId).clientId, light: verdict.light === "green" ? "green" : "amber" }];
      }),
    }));

  return { swaps, spares, loadList: [...fromStock].sort(byPotNumber), handOns, returnsToStock, problems };
}

/**
 * Suggest a pot for every spot on the route. She edits the result; `checkPlan` reports on it.
 *
 * Greedy, in driving order. For each spot, among pots in the car (unused stock + pots collected
 * at earlier stops) and never red, prefer:
 *   1. green over amber,
 *   2. a pot already in the car (hand-on) over loading another from stock,
 *   3. the pot that fits the fewest later spots (keep flexible pots for later),
 *   4. pot number, so the same input always gives the same plan.
 * A spot nothing fits stays empty, and `checkPlan` reports it as unfilled.
 * Greedy can miss a full plan in rare tight cases; her edit + check is the safety net.
 */
export interface SuggestOptions {
  readonly spares?: number;
  /** Her edits: these spots keep exactly this pot; everything else is suggested around them. */
  readonly pinned?: ReadonlyMap<string, string>;
}

export function suggestPlan(route: Route, input: PlannerInput, options: SuggestOptions = {}): DayPlan {
  const { catalog, history, assessor } = input;
  const routeIndex = firstIndexByPosition(route.slotIds);
  const stock = inStock(catalog, history);
  const collectedOnRoute = route.slotIds.flatMap((slotId) => {
    const occupant = history.occupantOf(slotId)?.potId;
    return occupant ? [occupant] : [];
  });

  // Every candidate pot assessed once against every spot on the route.
  const lightOf = new Map<string, Verdict["light"]>();
  const key = (potId: string, i: number) => `${potId}\u0000${i}`;
  for (const potId of [...stock, ...collectedOnRoute]) {
    route.slotIds.forEach((slotId, i) => {
      const { source } = sourceAt(potId, i, history, routeIndex);
      lightOf.set(key(potId, i), verdictFor(assessor, potId, slotId, route.on, source).light);
    });
  }
  const fitsLater = (potId: string, after: number): number =>
    route.slotIds.reduce((n, _, j) => (j > after && lightOf.get(key(potId, j)) !== "red" ? n + 1 : n), 0);

  const pinned = options.pinned ?? new Map<string, string>();
  // Pinned pots are spoken for before any suggestion is made.
  const used = new Set<string>(pinned.values());
  const swaps = route.slotIds.map((slotId, i) => {
    const pin = pinned.get(slotId);
    if (pin) return { slotId, potId: pin };
    const inCar = [
      ...stock.map((potId) => ({ potId, handOn: false })),
      ...collectedOnRoute
        .filter((potId) => sourceAt(potId, i, history, routeIndex).source === "collected-earlier")
        .map((potId) => ({ potId, handOn: true })),
    ].filter(({ potId }) => !used.has(potId) && lightOf.get(key(potId, i)) !== "red");

    inCar.sort(
      (a, b) =>
        Number(lightOf.get(key(a.potId, i)) === "amber") - Number(lightOf.get(key(b.potId, i)) === "amber") ||
        Number(b.handOn) - Number(a.handOn) ||
        fitsLater(a.potId, i) - fitsLater(b.potId, i) ||
        byPotNumber(a.potId, b.potId),
    );

    const choice = inCar[0]?.potId ?? null;
    if (choice) used.add(choice);
    return { slotId, potId: choice };
  });

  const greenCount = (potId: string) =>
    route.slotIds.reduce((n, _, i) => (lightOf.get(key(potId, i)) === "green" ? n + 1 : n), 0);
  const spares = stock
    .filter((potId) => !used.has(potId))
    .sort((a, b) => greenCount(b) - greenCount(a) || byPotNumber(a, b))
    .slice(0, Math.max(0, options.spares ?? 0));

  return { on: route.on, swaps, spares };
}

export interface Candidate {
  readonly potId: string;
  readonly source: Exclude<PotSource, "not-in-car">;
  readonly verdict: Verdict;
}

/**
 * Every pot that could go into the spot at route position `index`, best first: what the spot
 * sheet lists when she taps a spot. Pots in the car only (stock + collected earlier on the
 * route). Red pots are included at the end, so she can see why they're out, but the sheet
 * marks them. Same ordering as the suggestion: green, then amber, then red; a hand-on before
 * stock; then pot number.
 */
export function rankCandidates(route: Route, index: number, input: PlannerInput): Candidate[] {
  const { catalog, history, assessor } = input;
  const slotId = route.slotIds[index];
  if (slotId === undefined) return [];
  const routeIndex = firstIndexByPosition(route.slotIds);
  const stock = inStock(catalog, history);
  const handOns = route.slotIds
    .slice(0, index)
    .flatMap((s) => {
      const occupant = history.occupantOf(s)?.potId;
      return occupant ? [occupant] : [];
    })
    .filter((potId) => sourceAt(potId, index, history, routeIndex).source === "collected-earlier");

  const rank = { green: 0, amber: 1, red: 2 } as const;
  return [
    ...handOns.map((potId) => ({ potId, source: "collected-earlier" as const })),
    ...stock.map((potId) => ({ potId, source: "stock" as const })),
  ]
    .map(({ potId, source }) => ({ potId, source, verdict: verdictFor(assessor, potId, slotId, route.on, source) }))
    .sort(
      (a, b) =>
        rank[a.verdict.light] - rank[b.verdict.light] ||
        Number(b.source === "collected-earlier") - Number(a.source === "collected-earlier") ||
        byPotNumber(a.potId, b.potId),
    );
}
